import React from "react"
import { PageProps } from "gatsby"
import { useIntl } from "gatsby-plugin-intl"
import * as styles from "./nexus-mentis.module.scss"
import classNames from "classnames"

import SEO from "../../../components/seo"
import ProjectLayout from "../../../templates/project-page/ProjectLayout"
import ProjectGrid from "../../../components/UI/ProjectGrid"
import Image from "../../../components/UI/Image"


const NexusMentis: React.FC<PageProps> = ({ location }) => {
	const intl = useIntl()

	const researchList = [
		{ title: "Interviews with psychotherapists and psychiatrists" },
		{ title: "Analyzing existing solutions on the market (e.g., electronic medical records and mood tracking applications)" },
		{ title: "Examining medical standards for medication selection and compatibility testing systems" },
		{ title: "Physicians spend up to 30% of their time on paperwork and checking drug interactions manually" },
		{ title: "Patients rarely keep self-monitoring diaries regularly without a user-friendly interface" },
		{ title: "Most apps are not integrated with clinical processes" },
	]

	return (
		<ProjectLayout
			title={`NexusMentis: \nAI-based Decision Support System For Psychiatrists & Their Patients `}
			date="2022-2023"
			titleColor="dark"
			desktopImage="/images/projects/nexus-mentis/nm-title.png"
			mobileImage="/images/projects/nexus-mentis/m-nm-title.png"
			description="This project is sponsored by a private investor who knew first hand about the challenges modern psychiatry faces in finding the right timely treatment because of a lack of data and dispersed knowledge. It is how an idea was born to involve artificial intelligence that would help to reduce the time for finding the best medication, and enable the exchange of experiences between doctors - all to contribute to people's well-being."
			client="Private Investor (UAE)"
			expretise={`Full-cycle development \nDesign support \nAnalytics`}
			projectImage="/images/projects/nexus-mentis/nexus-mentis.png"
			location={location}
			back="dark"
		>
			<SEO
				language={intl.locale}
				description={intl.formatMessage({ id: "projects-new.list.nexus.description" })}
				title={intl.formatMessage({ id: "projects-new.list.nexus.metatitle" })}
				pathname={location.pathname}
			/>
			{/* Дополнительный контент */}
			<section className={styles.objective}>
				<h2>Objective</h2>
				<p className={styles.subtitle}>The client’s idea was to:</p>
				<ProjectGrid
					number
					listItems={[
						{ title: "Deliver a solution that gives AI-based recommendations on the most suitable treatment, reduces the number of insufficient prescriptions, and allows the exchange of experiences between doctors." },
						{ title: "Develop an expert system that analyzes millions of combinations and offers the best matching medicine from the knowledge base." },
						{ title: "Automate a doctor’s routine operations (a patient daily survey, visit journals, appointments table, etc.)" },
					]}
				/>
			</section>
			<section className={styles.research}>
				<h2>Research (competitive analysis)</h2>
				<div className={styles.researchContainer}>
					<h3>We conducted a study including:</h3>
					<ul className={styles.researchList}>
						{researchList.slice(0, 3).map((item, index) => (
							<li className={styles.researchItem} key={index}>{item.title}</li>
						))}
					</ul>
					<div className={styles.researchImg} />
					<h3>Key Insights:</h3>
					<ul className={classNames(styles.researchList, styles.researchListBlue)}>
						{researchList.slice(3, 6).map((item, index) => (
							<li className={styles.researchItem} key={index}>{item.title}</li>
						))}
					</ul>

				</div>
			</section>
			<section className={styles.solution}>
				<h2>Solution</h2>
				<p className={styles.solutionSubtitle}>Developed and launched a two-sided platform.</p>
				<div className={styles.solutionImg}><Image src="/images/projects/nexus-mentis/nm-solution.png" alt="Developed and launched a two-sided platform." fill /></div>
				<h3>A desktop application for the physician</h3>
				<div className={styles.solutionGrid}>
					<ProjectGrid
						listItems={[
							{ title: "Electronic medical history with the ability to track patient progression" },
							{ title: "AI-based tool for medication selection: takes into account diagnosis, current treatment, pharmacological interactions and risk of overdose" },
							{ title: "Calendar and patient notification system" },
						]}
					/>
				</div>
				<div className={styles.solutionImgBlock}>
					<div className={styles.solutionImgLarge}><Image src="/images/projects/nexus-mentis/nm-solution-1.png" alt="Aplication interface" fill /></div>
					<div className={styles.solutionImgLarge}><Image src="/images/projects/nexus-mentis/nm-solution-2.png" alt="Aplication interface" fill /></div>
				</div>
				<h3>Mobile app for the patient</h3>
				<ProjectGrid
					listItems={[
						{ title: "Online appointment booking" },
						{ title: "Daily survey to assess status - anxiety level, sleep, mood, etc." },
						{ title: "Reminder system and automatic sending of data to the doctor" },
					]}
				/>
				<div className={styles.mobileBlock}>
					<p className={styles.mobileText}>
						AI-module is based on medical databases and standards, and is internally validated using cases from the practice of consulting doctors.
					</p>
					<div className={styles.mobileImg}>
						<Image src="/images/projects/nexus-mentis/nm-mobile.png" alt="Mobile app" fill />
					</div>
				</div>
			</section>
			<section>
				<h2>Result</h2>
				<p className={styles.resultText}>NexusMentis is a unique start-up in the pre-seed phase that works as a decision support recommendation system for doctors through the use of its Smart Pharmacy and diagnostics tool.</p>
				<div className={styles.resultGrid}>
					<ProjectGrid
						bg="blue"
						listItems={[
							{ title: "Inventale developed a recommendation engine for matching diagnoses and medicines, and offering the most suitable medications." },
							{ title: "The recommendation engine has been trained and tested on a database." },
							{ title: "The beta version of the system was presented to the UAE Ministry Of Health & Prevention (MoHAP) and received very positive feedback, expert opinion and their pieces of advice." },
						]}
					/>
				</div>
			</section>
		</ProjectLayout>

	)
}

export default NexusMentis;
